import React, { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  useFilamentSpools,
  useUpdateFilamentSpool,
  useDeleteFilamentSpool,
  FILAMENT_MATERIALS,
} from '../../hooks/useFilamentSpools'
import { BackLink } from '../../components/BackLink'
import { useToast } from '../../components/ToastContext'

export default function FilamentEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: spools, isLoading } = useFilamentSpools()
  const updateMutation = useUpdateFilamentSpool()
  const deleteMutation = useDeleteFilamentSpool()
  const { toast } = useToast()

  const existing = spools?.find((s) => s.id === id)

  const initial = existing
    ? {
        brand: existing.brand ?? '',
        material: existing.material,
        color: existing.color,
        colorHex: existing.color_hex ?? '#000000',
        purchasePrice:
          existing.purchase_price != null ? String(existing.purchase_price) : '',
        purchaseDate: existing.purchase_date ?? '',
        originalWeight: String(existing.original_weight_grams),
        currentWeight: String(existing.current_weight_grams),
        status: existing.status,
      }
    : null

  const [draft, setDraft] = useState(initial)
  const [errorMessage, setErrorMessage] = useState('')

  if (isLoading) return <div style={{ padding: 'var(--space-6)' }}>Loading…</div>
  if (!existing || !draft) return <div style={{ padding: 'var(--space-6)' }}>Spool not found.</div>

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')
    try {
      await updateMutation.mutateAsync({
        id: existing.id,
        brand: draft.brand || null,
        material: draft.material,
        color: draft.color,
        color_hex: draft.colorHex || null,
        purchase_price: draft.purchasePrice ? Number(draft.purchasePrice) : null,
        purchase_date: draft.purchaseDate || null,
        original_weight_grams: Number(draft.originalWeight),
        current_weight_grams: Number(draft.currentWeight),
        status: draft.status as 'in-use' | 'finished' | 'archived',
      })
      navigate('/prints/filament')
    } catch (err) {
      setErrorMessage((err as Error).message)
    }
  }

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Delete spool "${existing.brand ?? ''} ${existing.color}"? This cannot be undone if any prints reference it.`,
      )
    )
      return
    try {
      await deleteMutation.mutateAsync(existing.id)
      toast({ tone: 'success', text: 'Spool deleted' })
      navigate('/prints/filament')
    } catch (err) {
      setErrorMessage((err as Error).message)
    }
  }

  const set =
    (key: keyof NonNullable<typeof draft>) =>
    (v: string) =>
      setDraft({ ...draft, [key]: v })

  // Siblings: other spools with the same brand/material/color (regardless of
  // status), so the user can jump between rolls of the same color.
  const siblings = (spools ?? []).filter(
    (s) =>
      s.id !== existing.id &&
      (s.brand ?? '').trim().toLowerCase() === (existing.brand ?? '').trim().toLowerCase() &&
      s.material === existing.material &&
      s.color.trim().toLowerCase() === existing.color.trim().toLowerCase(),
  )

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 560 }}>
      <BackLink fallback="/prints/filament" />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-3)' }}>
        <h1>Edit spool</h1>
        <Link
          to={`/prints/filament/new?fromSpoolId=${existing.id}`}
          style={{
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          + Buy more like this
        </Link>
      </div>
      <p
        style={{
          color: 'var(--color-text-muted)',
          marginTop: 'var(--space-2)',
          fontSize: 'var(--font-size-sm)',
        }}
      >
        Tip: put the spool on a kitchen scale and update Current weight to recalibrate after some prints — corrects drift from slicer estimates.
      </p>

      {siblings.length > 0 && (
        <div
          style={{
            background: 'var(--color-surface-2)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3)',
            marginTop: 'var(--space-4)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--color-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 'var(--space-2)',
            }}
          >
            Other rolls of this color ({siblings.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            {siblings.map((s) => (
              <Link
                key={s.id}
                to={`/prints/filament/${s.id}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: 'var(--space-2)',
                  color: 'var(--color-text)',
                  textDecoration: 'none',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <span style={{ color: s.status === 'in-use' ? 'var(--color-text)' : 'var(--color-text-faint)' }}>
                  {s.current_weight_grams}g / {s.original_weight_grams}g
                  {s.status !== 'in-use' && ` · ${s.status}`}
                </span>
                <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>Edit →</span>
              </Link>
            ))}
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} style={{ marginTop: 'var(--space-6)' }}>
        <Field label="Brand" value={draft.brand} onChange={set('brand')} />
        <Field
          label="Material"
          value={draft.material}
          onChange={set('material')}
          select={FILAMENT_MATERIALS as unknown as string[]}
        />
        <Field label="Color name" value={draft.color} onChange={set('color')} required />
        <Field label="Color swatch" value={draft.colorHex} onChange={set('colorHex')} type="color" />
        <Field
          label="Purchase price ($)"
          value={draft.purchasePrice}
          onChange={set('purchasePrice')}
          type="decimal"
        />
        <Field
          label="Purchase date"
          value={draft.purchaseDate}
          onChange={set('purchaseDate')}
          type="date"
        />
        <Field
          label="Original weight (g)"
          value={draft.originalWeight}
          onChange={set('originalWeight')}
          type="decimal"
        />
        <Field
          label="Current weight (g)"
          value={draft.currentWeight}
          onChange={set('currentWeight')}
          type="decimal"
          hint="Edit this to reweigh — corrects slicer-estimate drift"
        />
        <Field
          label="Status"
          value={draft.status}
          onChange={set('status')}
          select={['in-use', 'finished', 'archived']}
        />

        <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-6)' }}>
          <button
            type="submit"
            disabled={updateMutation.isPending}
            style={{
              padding: 'var(--space-3) var(--space-5)',
              background: 'var(--color-primary)',
              color: 'white',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
            }}
          >
            {updateMutation.isPending ? 'Saving…' : 'Save changes'}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            style={{
              padding: 'var(--space-3) var(--space-5)',
              background: 'transparent',
              color: 'var(--color-danger)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
            }}
          >
            Delete
          </button>
        </div>

        {errorMessage && (
          <p
            style={{
              marginTop: 'var(--space-3)',
              color: 'var(--color-danger)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {errorMessage}
          </p>
        )}
      </form>
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  hint,
  required,
  select,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: 'text' | 'date' | 'decimal' | 'color'
  hint?: string
  required?: boolean
  select?: string[]
}) {
  const commonStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
  }
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label
        style={{
          display: 'block',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          marginBottom: 'var(--space-1)',
        }}
      >
        {label} {required && <span style={{ color: 'var(--color-danger)' }}>*</span>}
      </label>
      {select ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} style={commonStyle}>
          {select.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type === 'date' ? 'date' : type === 'color' ? 'color' : 'text'}
          inputMode={type === 'decimal' ? 'decimal' : undefined}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={
            type === 'color'
              ? { ...commonStyle, padding: 'var(--space-1)', height: 44, cursor: 'pointer' }
              : commonStyle
          }
        />
      )}
      {hint && (
        <p
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-faint)',
            marginTop: 'var(--space-1)',
          }}
        >
          {hint}
        </p>
      )}
    </div>
  )
}
